<template>
  <!--
    ArtifactDetail SECTION — Metric artifact, "Opportunity Sync" tab.

    WHAT: Configures the metric→worklist-opportunity bridge (create / expire /
      priority / assignment strategies) — fully non-DA config. Wraps the reused
      OpportunityConfigPanel.

    CONTRACT (props { record, artifact, context }):
      READS  record.opportunity_config (deep-cloned into local `cfg`),
             context.opportunityPrimitives (wrapper workflows of role='opportunity' DAs),
             context.opportunityPatientFlags (flags usable in priority rules),
             context.dossierTemplates (patient Hermes templates, for on_create_dossiers),
             context.metrics + record.metric_name_slug (supersession pickers).
      EMITS  patch({ opportunity_config: cfg }) on the panel's @save.

    NOTES:
      - The panel owns its own Save/Revert affordance; onSave emits `patch` (the
        shell debounces the PATCH) and shows a transient saving/savedFlash state.
        onRevert re-clones from record.opportunity_config.
      - No hydrating guard needed — this section patches only on explicit @save,
        not on every input change.
      - selfChrome: true in the registry — the panel draws its own card, so
        ArtifactDetail does NOT wrap it.
  -->
  <OpportunityConfigPanel
    v-model="cfg"
    :saving="saving"
    :saved-flash="savedFlash"
    :save-error="saveError"
    :primitives="context.opportunityPrimitives || []"
    :workflows="context.liveWorkflows || []"
    :patient-flags="context.opportunityPatientFlags || []"
    :dossier-templates="context.dossierTemplates || []"
    :metrics="context.metrics || []"
    :self-slug="selfSlug"
    @save="onSave"
    @revert="onRevert"
  />
</template>

<script>
import OpportunityConfigPanel from '@/components/Admin/MetricStudio/panels/OpportunityConfigPanel.vue';

export default {
  name: 'OpportunitySection',
  components: { OpportunityConfigPanel },
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch'],
  data() {
    return {
      cfg: JSON.parse(JSON.stringify(this.record.opportunity_config || {})),
      saving: false,
      savedFlash: false,
      saveError: false,
    };
  },
  computed: {
    // This metric's own slug, so the supersession pickers can exclude it.
    // Sourced from context.metrics (the LIST serializer), not `record` — the
    // metric DETAIL serializer does not expose metric_name_slug.
    selfSlug() {
      const me = (this.context.metrics || []).find((m) => m.id === this.record.id);
      return (me && me.metric_name_slug) || '';
    },
  },
  methods: {
    onSave() {
      this.saving = true;
      this.saveError = false;
      this.$emit('patch', { opportunity_config: this.cfg || {} }, {
        immediate: true,
        onSuccess: (saved) => {
          this.cfg = JSON.parse(JSON.stringify(saved.opportunity_config || {}));
          this.saving = false;
          this.savedFlash = true;
          setTimeout(() => { this.savedFlash = false; }, 2500);
        },
        onError: () => {
          this.saving = false;
          this.saveError = true;
        },
      });
    },
    onRevert() {
      this.cfg = JSON.parse(JSON.stringify(this.record.opportunity_config || {}));
    },
  },
};
</script>
